import {SelectCreatable} from '@wandb/weave/components/Form/Select';
import {useGetTraceServerClientContext} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientContext';
import React, {useEffect, useState} from 'react';

import {WeaveMetricThresholdActions} from '../store';
import {type MetricPathOption} from '../types';

// Distinct values per dropdown; spans `group_by` returns one row per value.
const SPAN_FILTER_OPTION_LIMIT = 1000;
// Recently-seen values only, so the group_by scans a sort-key-pruned window
// instead of the project's full span history.
const SPAN_FILTER_LOOKBACK_MS = 24 * 60 * 60 * 1000;

type SpanFilterColumn = 'operation_name' | 'span_name' | 'agent_name';

type SpanFilterSelectorProps = {
  projectId: string;
  operationName?: string;
  spanName?: string;
  agentName?: string;
};

/**
 * Optional equality filters scoping a spans-source alert to a span type.
 * Each field maps to a spans column (operation_name / span_name / agent_name);
 * multiple are AND-combined and leaving all empty evaluates every span.
 */
export const SpanFilterSelector = ({
  projectId,
  operationName,
  spanName,
  agentName,
}: SpanFilterSelectorProps) => {
  const fields = [
    {
      label: 'Operation name',
      field: 'operation_name',
      value: operationName,
      onChange: WeaveMetricThresholdActions.setSpanOperationName,
    },
    {
      label: 'Span name',
      field: 'span_name',
      value: spanName,
      onChange: WeaveMetricThresholdActions.setSpanName,
    },
    {
      label: 'Agent name',
      field: 'agent_name',
      value: agentName,
      onChange: WeaveMetricThresholdActions.setSpanAgentName,
    },
  ] as const;
  return (
    <div className="flex flex-col gap-8">
      {fields.map(field => (
        <SpanFilterField key={field.field} projectId={projectId} {...field} />
      ))}
    </div>
  );
};

type SpanFilterFieldProps = {
  label: string;
  field: SpanFilterColumn;
  projectId: string;
  value?: string;
  onChange: (value: string) => void;
};

const SpanFilterField = ({
  label,
  field,
  projectId,
  value,
  onChange,
}: SpanFilterFieldProps) => {
  // These filters are optional and usually left blank, so only fetch the
  // distinct-value options once the user actually opens the dropdown.
  const [opened, setOpened] = useState(false);
  const {options, loading} = useSpanFilterOptions(projectId, field, opened);
  const selected: MetricPathOption | null = value
    ? {value, label: value}
    : null;
  return (
    <div>
      <div className="mb-4 text-sm text-moon-600 dark:text-wb-text-secondary">
        {label}
      </div>
      <SelectCreatable<MetricPathOption, false>
        value={selected}
        options={options}
        isLoading={loading}
        onChange={newValue => onChange(newValue?.value || '')}
        onMenuOpen={() => setOpened(true)}
        isClearable
        placeholder={`Any ${label.toLowerCase()}`}
        menuPortalTarget={document.body}
        formatCreateLabel={(input: string) => `Use "${input}"`}
      />
    </div>
  );
};

/** Distinct values for a spans column, fetched via a raw agent-spans group_by
 *  query once `enabled`. Deliberately not the Agents-tab facet hook: alerts
 *  evaluate every span, so options must not inherit its default-agent-name
 *  exclusions. Stays creatable so a typed value works with no logged spans. */
const useSpanFilterOptions = (
  projectId: string,
  field: SpanFilterColumn,
  enabled: boolean
) => {
  const getTsClient = useGetTraceServerClientContext();
  const [options, setOptions] = useState<MetricPathOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let active = true;
    setLoading(true);
    getTsClient()
      .agentSpansQuery({
        project_id: projectId,
        group_by: [{source: 'column', key: field}],
        sort_by: [{field: 'span_count', direction: 'desc'}],
        limit: SPAN_FILTER_OPTION_LIMIT,
        started_after: new Date(
          Date.now() - SPAN_FILTER_LOOKBACK_MS
        ).toISOString(),
      })
      .then(res => {
        if (!active) {
          return;
        }
        setOptions(
          res.groups
            .map(row => row.group_keys[field])
            .filter((v): v is string => typeof v === 'string' && v !== '')
            .map(v => ({value: v, label: v}))
        );
      })
      .catch(() => {
        if (active) {
          setOptions([]);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [enabled, getTsClient, projectId, field]);

  return {options, loading};
};
